/** Tab 13: Checklist */
import { useState } from 'react'
import {
  Button, Card, Input, Modal, Space, Table, Tag,
} from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  joiningApi,
  type JoiningDetail,
  type ChecklistItemRow,
  type ChecklistItemStatus,
} from '@/features/hr/joiningApi'

interface Props { detail: JoiningDetail }

const STATUS_COLOR: Record<ChecklistItemStatus, string> = {
  PENDING: 'default',
  DONE: 'green',
  NA: 'blue',
  WAIVED: 'orange',
}

export default function ChecklistTab({ detail }: Props) {
  const qc = useQueryClient()
  const [waiveOpen, setWaiveOpen] = useState(false)
  const [waiveItemId, setWaiveItemId] = useState<string | null>(null)
  const [waiveReason, setWaiveReason] = useState('')

  const updateItem = useMutation({
    mutationFn: (args: {
      itemId: string
      status: ChecklistItemStatus
      reason?: string
    }) => joiningApi.updateChecklistItem(args.itemId, args.status, args.reason),
    onSuccess: () => {
      setWaiveOpen(false)
      setWaiveItemId(null)
      setWaiveReason('')
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const cols = [
    {
      title: 'Item',
      render: (_: unknown, row: ChecklistItemRow) => (
        <Space>
          <Tag>{row.category}</Tag>
          <span>{row.item_name}</span>
          {row.required_before_activation && <Tag color="red">Required</Tag>}
        </Space>
      ),
    },
    { title: 'Owner', dataIndex: 'owner_role' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: ChecklistItemStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      title: 'Completed At',
      dataIndex: 'completed_at',
      render: (v: string | null) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'Actions',
      render: (_: unknown, row: ChecklistItemRow) => (
        <Space size="small">
          <Button
            size="small"
            type="primary"
            onClick={() => updateItem.mutate({ itemId: row.id, status: 'DONE' })}
          >
            Mark Done
          </Button>
          <Button
            size="small"
            onClick={() => updateItem.mutate({ itemId: row.id, status: 'NA' })}
          >
            Mark NA
          </Button>
          <Button
            size="small"
            onClick={() => {
              setWaiveItemId(row.id)
              setWaiveOpen(true)
            }}
          >
            Waive
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <Card size="small" title="Activation Checklist">
      <Table<ChecklistItemRow>
        rowKey="id"
        size="small"
        dataSource={detail.checklist}
        columns={cols}
        pagination={false}
      />

      <Modal
        title="Waive Checklist Item"
        open={waiveOpen}
        onCancel={() => {
          setWaiveOpen(false)
          setWaiveItemId(null)
        }}
        onOk={() => {
          if (waiveItemId) {
            updateItem.mutate({
              itemId: waiveItemId,
              status: 'WAIVED',
              reason: waiveReason,
            })
          }
        }}
        confirmLoading={updateItem.isPending}
      >
        <Input.TextArea
          rows={3}
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
          placeholder="Reason for waiver"
        />
      </Modal>
    </Card>
  )
}
